import { useAuth } from "@healthos/auth/react";
import { queryKeys, readSupportSignal } from "@healthos/client-data";
import { usePrybar } from "@prybar/core/use-prybar";
import { useQueryClient } from "@tanstack/react-query";
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { readEnv } from "../env";
import { useTheme } from "../lib/theme";
import { handlePrybarNavigate } from "./highlight";
import { PrybarIcon } from "./icon";
import { installPrybarFetchAuth } from "./install-fetch-auth";
import {
  collectOnScreenTargets,
  describePlace,
  PRYBAR_PLACES,
  PRYBAR_SECTIONS,
  type PrybarOnScreenTarget,
} from "./navigation";
import { healthOsRenderers } from "./renderers";
import { usePrybarScreenData } from "./screen-context";
import { Sound } from "./sound";
import { suggestionsFor } from "./suggestions";
import { healthOsPrybarTheme } from "./theme";
import "./prybar.css";

const PrybarCommandBar = lazy(() =>
  import("@prybar/core/client").then((m) => ({ default: m.PrybarCommandBar })),
);

// ["microsteps"] is a dev-only prefix with no shared canonical key yet.
const REFRESH_KEYS = [
  queryKeys.ambient,
  queryKeys.outcome,
  ["microsteps"] as const,

  // Memory and reasoning surfaces: a Prybar turn can write memory
  // (memory.remember/update/forget) or complete steps that change what
  // Heartbeat shows, so the maps and the Today card follow the conversation.
  queryKeys.whmaGraph,
  queryKeys.heartbeatGraph,
  queryKeys.heartbeatPasses,
  queryKeys.heartbeatCard,
];

export function HealthOsCommandBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { state: authState } = useAuth();
  // Keys the SDK's persisted previous-session transcript per member; without it
  // every member on a shared browser reads the last member's conversation.
  const authedUserId = authState.status === "signedIn" ? authState.user.profile.sub : undefined;
  const apiBase = readEnv().apiBaseUrl;
  const screenData = usePrybarScreenData(location.pathname);
  // a voice wake plays its own earcon, so the bar-open chime must not double up
  const voiceWakeOpenRef = useRef(false);

  useEffect(() => {
    installPrybarFetchAuth("/api/prybar/");
  }, []);

  const refresh = useCallback(() => {
    for (const key of REFRESH_KEYS) queryClient.invalidateQueries({ queryKey: key });
  }, [queryClient]);

  const onAction = useCallback(
    async (action: { toolName: string; params?: Record<string, unknown> }) => {
      await fetch(`${apiBase}/api/prybar/action`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tool: action.toolName, params: action.params ?? {} }),
      }).finally(() => refresh());
    },
    [apiBase, refresh],
  );

  // Memoised on route + screen so they change identity only when the user
  // moves or the screen updates. usePrybar's config memo depends on these, so
  // suggestions and appContext stay live (the SDK otherwise freezes them at
  // mount) without re-creating the config on every render.
  const route = location.pathname;
  const suggestions = useMemo(() => suggestionsFor(route, screenData), [route, screenData]);

  // Sections Prybar can scroll to right now, discovered live from the DOM after
  // the screen commits. A newly-marked section shows up here with no other edit.
  const [onScreen, setOnScreen] = useState<PrybarOnScreenTarget[]>([]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: route/screenData are re-collect triggers (the DOM may have new sections), not values read here
  useEffect(() => {
    setOnScreen(collectOnScreenTargets());
  }, [route, screenData]);

  const appContext = useMemo(
    () => ({
      route,
      data: {
        route,
        viewing: describePlace(route),
        // What Prybar can drive: pages it can open, important cross-page
        // sections, and sections on this screen it can scroll to. The model
        // passes a `route` from any of these to app.navigate.
        places: PRYBAR_PLACES,
        sections: PRYBAR_SECTIONS,
        onScreen,
        ...screenData,
      },
    }),
    [route, screenData, onScreen],
  );

  const { resolved: scheme } = useTheme();
  const theme = useMemo(() => healthOsPrybarTheme(scheme), [scheme]);

  const prybar = usePrybar({
    chatEndpoint: `${apiBase}/api/prybar/stream`,
    // The assistant is the surface a member is most likely to type distress into.
    onResponse: readSupportSignal,
    ...(authedUserId ? { userId: authedUserId } : {}),
    branding: "HealthOS",
    theme,
    IconComponent: PrybarIcon,
    strings: {
      inputPlaceholder: "Ask why, flag what's off, or get help right now",
      emptyHint:
        "Ask why something's on your plan, fix what isn't working, or say what you need right now",
    },
    suggestions,
    appContext,
    componentRenderers: healthOsRenderers,
    // The self-hosted "Hey Thrive" wake model. Inert until the server enables
    // voice (voiceEnabled flows from the server config response, gated by
    // PRYBAR_VOICE), so this never fetches the model while voice is off.
    // The ?v= is a cache-buster: the detector caches the model in IndexedDB by
    // URL, so bump this whenever hey_thrive.onnx is retrained.
    voice: { wakeWordModelUrl: "/wake/hey_thrive.onnx?v=7", wakeWordThreshold: 0.5 },
    onVoiceCue: (cue: "wake" | "answer" | "confirm" | "error") => {
      if (cue === "wake") {
        voiceWakeOpenRef.current = true;
        Sound.wake();
      } else if (cue === "answer") Sound.done();
      else if (cue === "confirm") Sound.ask();
      else Sound.cancel();
    },
    navigate: (path: string) => navigate(path),
    // A pure "open my plan" navigates then closes the bar; a turn that also
    // answers, shows a card, or asks to confirm stays open (handled in the SDK).
    closeAfterNavigation: true,
    // Joey reads voice-triggered replies aloud (server gates on PRYBAR_NARRATE).
    narrationEndpoint: `${apiBase}/api/prybar/narrate`,
    onNavigate: handlePrybarNavigate,
    onAction,
    onToolResult: refresh,
  });

  // The docked trigger (sidebar / mobile header) opens the bar via a window
  // event, so the trigger's placement stays decoupled from the bar.
  const open = prybar.open;
  useEffect(() => {
    const handler = () => open();
    window.addEventListener("prybar:open", handler);
    return () => window.removeEventListener("prybar:open", handler);
  }, [open]);

  // Soft open chime on the design's Web-Audio voice when the bar appears.
  const isOpen = prybar.isOpen;
  useEffect(() => {
    if (!isOpen) {
      voiceWakeOpenRef.current = false;
      return;
    }
    if (voiceWakeOpenRef.current) {
      voiceWakeOpenRef.current = false;
      return;
    }
    Sound.open();
  }, [isOpen]);

  return (
    <Suspense fallback={null}>
      <PrybarCommandBar
        config={prybar.config}
        isOpen={prybar.isOpen}
        IconComponent={prybar.IconComponent}
        userId={prybar.userId}
        onOpen={prybar.open}
      />
    </Suspense>
  );
}
